/**
 * Analytics helper.
 *
 * Lightweight no-op–safe wrapper around any analytics pixels available on the
 * page. С 21.08.2026 наружу уходит только Яндекс.Метрика: пиксель Meta,
 * Google Analytics, Microsoft Clarity и счётчик Mail.ru убраны с сайта.
 * Missing pixels are simply skipped, so it is safe to call from anywhere.
 */

// Admin "Аналитика" (funnel + calculator breakdown, server.py
// admin_get_funnel_stats / admin_get_calculator_stats) reads db.events
// filtered by a specific vocabulary: visit_landing, click_calculate,
// signup_complete, payment_start, payment_success, calc_open, calc_submit —
// and, for the calculator breakdown, meta.slug. trackEvent() call sites
// across the app use their own more descriptive names (calculator_opened,
// checkout_started, ...) that never matched, and — more fundamentally —
// this function never sent anything to the backend at all, only to
// GA/Facebook Pixel/Metrika. db.events was essentially only ever populated
// by a handful of server-side events, which is why the funnel/calculator
// cards always read ~0. Translate the known names; anything without a
// mapping is still forwarded under its own name (captured for later use,
// just not counted on those two specific cards).
const BACKEND_EVENT_MAP = {
  calculator_opened: { name: 'calc_open', metaKeys: { calculator_type: 'slug' } },
  checkout_started: { name: 'payment_start' },
  signup_completed: { name: 'signup_complete' },
  payment_success: { name: 'payment_success' },
};

import { METRIKA_ID_NUM } from '@/lib/siteConfig';

// Счётчик берётся из паспорта сайта (REACT_APP_METRIKA_ID); null — без счётчика.
const YM_COUNTER_ID = METRIKA_ID_NUM

// Прямая цель Метрики без похода на бэкенд: для кликов и покупок, где
// нужен только счётчик. params уходят как параметры визита (order_price
// и currency Метрика понимает как денежные).
/**
 * ClientID счётчика Метрики. Нужен серверу, чтобы привязать офлайн-конверсию
 * purchase_paid к визиту этого человека. Метрика отдаёт значение через колбэк;
 * если её нет, она выключена или молчит — отдаём null и никого не задерживаем.
 * Оплата не должна ждать аналитику.
 */
export function ymClientIdAsync(maxWaitMs = 300) {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') return resolve(null);
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value || null);
    };
    const timer = setTimeout(() => finish(null), maxWaitMs);
    try {
      const id = window.ymYandexMetrikaId || YM_COUNTER_ID;
      if (!window.ym || !id) {
        clearTimeout(timer);
        return finish(null);
      }
      window.ym(id, 'getClientID', (clientID) => {
        clearTimeout(timer);
        finish(clientID ? String(clientID) : null);
      });
    } catch (_e) {
      clearTimeout(timer);
      finish(null);
    }
  });
}

export function ymGoal(name, params) {
  if (typeof window === 'undefined') return;
  try {
    const id = window.ymYandexMetrikaId || YM_COUNTER_ID;
    if (window.ym && id) window.ym(id, 'reachGoal', name, params || undefined);
  } catch (_e) {}
}

/**
 * Как ymGoal, но с колбэком. Даёт запросу к Метрике фору (по умолчанию 250 мс)
 * и только потом выполняет действие — переход. Если Метрика выключена, не
 * загрузилась или отвечает медленно, действие выполняется всё равно: человек
 * не должен чувствовать задержки на кнопке.
 */
export function ymGoalThen(name, params, done, maxWaitMs = 250) {
  let called = false;
  const run = () => {
    if (called) return;
    called = true;
    try { done(); } catch (_e) {}
  };
  if (typeof window === 'undefined') { run(); return; }
  try {
    const id = window.ymYandexMetrikaId || YM_COUNTER_ID;
    if (window.ym && id) {
      window.ym(id, 'reachGoal', name, params || undefined, run);
      setTimeout(run, maxWaitMs);
      return;
    }
  } catch (_e) {}
  run();
}

function sendEventToBackend(name, params) {
  try {
    // Без REACT_APP_BACKEND_URL — тот же домен: прод-сервер (server.js)
    // проксирует /api/* на бэкенд, как и для всех остальных запросов сайта
    // (lib/backendUrl). Раньше пустая переменная молча глушила ВСЕ события
    // воронки, включая click_calculate. В тестах без переменной — тишина.
    let raw = (process.env.REACT_APP_BACKEND_URL || '').trim();
    if (raw === 'undefined' || raw === 'null') raw = '';
    if (!raw && process.env.NODE_ENV === 'test') return;
    const backendUrl = raw.replace(/\/+$/, '').replace(/\/api$/, '');
    const mapping = BACKEND_EVENT_MAP[name];
    const eventName = mapping ? mapping.name : name;
    let meta = params;
    if (mapping?.metaKeys) {
      meta = { ...params };
      for (const [from, to] of Object.entries(mapping.metaKeys)) {
        if (meta[from] !== undefined && meta[to] === undefined) {
          meta[to] = meta[from];
        }
      }
    }
    const token = localStorage.getItem('auth_token');
    fetch(`${backendUrl}/api/events/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ event_name: eventName, meta }),
      keepalive: true,
    }).catch(() => {});
  } catch (_e) {}
}

export function trackEvent(name, params = {}) {
  if (typeof window === 'undefined') return;

  try {
    const _ymId = window.ymYandexMetrikaId || YM_COUNTER_ID;
    if (window.ym && _ymId) {
      window.ym(_ymId, 'reachGoal', name, params);
    }
  } catch (_e) {}

  sendEventToBackend(name, params);

  // Debug-friendly log in development only
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.debug('[analytics]', name, params);
  }
}

export function trackPageView(path) {
  if (typeof window === 'undefined') return;
}

/**
 * Identify the current user for analytics platforms. No-op when no analytics
 * SDK is available — kept as a stable import so callers never crash.
 */
export function identifyUser(userId, traits = {}) {
  if (typeof window === 'undefined' || !userId) return;
  try {
    if (window.ym && window.ymYandexMetrikaId) {
      window.ym(window.ymYandexMetrikaId, 'userParams', { UserID: userId, ...traits });
    }
  } catch (_e) {}
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.debug('[analytics] identifyUser', userId, traits);
  }
}

export default { trackEvent, trackPageView, identifyUser };

